import type { ResolverInput, ResolverResult } from './types';

// Item 11 — Import Clients sub-product (Session 28, Phase 1 of 6).
//
// Completion criteria:
//   1. At least one row exists in `clients` with source matching an import
//      path prefix (`import_conversational`, `import_phone_contacts`,
//      `import_spreadsheet`, `import_competitor`). These source values will
//      start being written by Phases 2-5; today the query simply matches
//      nothing. Note: the `clients_source_check` constraint in migration 103
//      currently permits only `('manual','booking_page','import','kiosk')` —
//      extending it to the four `import_*` values is a dependency for
//      Phases 2-5, not Phase 1.
//   2. The user skipped the item with a reason. Both reasons count as
//      complete and are surfaced in `reason` for analytics / Bit re-surfacing.
//
// Skip-flag read pattern mirrors addTeamResolver (spec §5.8).
export async function resolveImportClientsItem(
  { businessId, supabase }: ResolverInput,
): Promise<ResolverResult> {
  const { data: biz, error: bizError } = await supabase
    .from('businesses')
    .select('onboarding_answers')
    .eq('id', businessId)
    .maybeSingle();

  if (bizError) return { complete: false, reason: `query error: ${bizError.message}` };

  const itemState = (biz?.onboarding_answers as {
    setup_guide?: {
      items?: Record<string, { skipped?: boolean; skipped_reason?: string | null }>;
    };
  } | null)?.setup_guide?.items?.import_clients;

  if (itemState?.skipped === true) {
    if (itemState.skipped_reason === 'not_applicable') {
      return { complete: true, reason: 'skipped_not_applicable' };
    }
    if (itemState.skipped_reason === 'deferred') {
      return { complete: true, reason: 'skipped_deferred' };
    }
    return { complete: true, reason: 'skipped' };
  }

  const { count, error } = await supabase
    .from('clients')
    .select('id', { count: 'exact', head: true })
    .eq('business_id', businessId)
    .like('source', 'import\\_%');

  if (error) return { complete: false, reason: `query error: ${error.message}` };
  if ((count ?? 0) > 0) return { complete: true, reason: 'imported' };
  return { complete: false, reason: 'no clients imported yet' };
}
